// Reconciliation ledger — /analytics/orgs/{org_id}/reconciliation. An ORG
// OWNER's per-period check that the accrual books match the money that actually
// moved: per payout period, the computed net owed (Expected) vs what was actually
// paid out (Received), with the SIGNED delta (Difference) and a
// reconciliation_status. Sits beside the payout ledger (what was PAID) and the
// settlement view (what is OWED) in the Earnings workspace.
//
// THIN presentation layer mirroring PayoutLedger (its own org-scoped useQuery,
// Card/CardHeader, honest loading/403/error/empty states, a LOCAL humanize):
// every figure — computed_net_owed / paid_out / delta (each Money, delta SIGNED),
// reconciliation_status, the unmatched/failed counts — is a real backend field
// rendered VERBATIM via the shared money formatter.
//
// THE THIN-FE RULE FOR THE DIFFERENCE (load-bearing): the Difference is the
// backend `delta` rendered AS-IS — it is NEVER a client recompute of
// (paid_out − computed_net_owed). The two can legitimately diverge (Stripe
// processing fees the computed net does not model, §3 of the 2c spec), so a
// client subtraction could lie. A `matched` period honestly shows a $0.00
// difference because the backend delta IS 0.00 there.
//
// CALM, not alarming (2c §2): a `short` period is the COMMON case (Stripe fees
// make paid_out < computed_net_owed systematically), and `over`/`pending`/
// `not_applicable` are routine bookkeeping states — NONE is an error. The status
// renders as a plain, calm label (NOT a destructive/red badge); only the genuine
// fetch-error affordance uses role=alert. A `matched` period reads a calm
// success tone; everything else reads neutral.
//
// LAYOUT: a Period | Expected | Received | Difference | Status table — the SAME
// chrome as the sibling SettlementLedger / PayoutLedger ledgers it sits beside in
// the Earnings workspace (#1432: a block layout beneath two tables read as
// visually divergent). Expected = computed_net_owed, Received = paid_out,
// Difference = the backend `delta` VERBATIM. A genuinely matched period honestly
// shows the same figure in Expected and Received with a $0.00 Difference — there
// is no "collapse" of the two cells (a fixed-column table compares side by side),
// so the row can never mislabel a non-zero delta as a clean match. A per-period
// reconciliation_note renders in its own honest sub-row beneath the period.
//
// Like PayoutLedger this view is ORG-scoped against a separate endpoint, so the
// component owns its OWN query (keyed by orgId, enabled only once an org id is
// resolved). The parent passes the resolved orgId and gates rendering on it. A
// non-owner facility manager gets a 403 here (owner / internal-platform-admin
// only) → an honest owner-only note, NOT the retryable "couldn't load" alert.

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@bw/shared/api";
import { formatDate, formatMoney } from "@bw/shared/utils";
import { Fragment } from "react";

import {
  getOrgReconciliation,
  type PeriodReconciliationResponse,
} from "@/libs/analyticsApi";

/** "not_applicable" -> "Not applicable", "in_transit" -> "In transit". Truthful
 * to the token; the raw machine token (with underscores) is NEVER shown
 * verbatim. Mirrors PayoutLedger's local humanizeStatus. */
function humanizeStatus(status: string): string {
  const normalized = status.trim().replace(/_/g, " ");
  if (!normalized) return status;
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

/**
 * CALM status tone. A `matched` period reads a settled, success tone (emerald);
 * EVERY other reconciliation state — short / over / pending / not_applicable —
 * reads a neutral, muted tone. We deliberately NEVER use a destructive/red tone:
 * a `short` is the routine Stripe-fee case (2c §3), so alarming red would cry
 * wolf. Genuine failures surface via the fetch-error affordance (role=alert),
 * never a status label.
 */
function statusTone(status: string): "success.main" | "text.secondary" {
  return status.trim().toLowerCase() === "matched"
    ? "success.main"
    : "text.secondary";
}

/**
 * Reconciliation for an org. `orgId` is resolved by the parent (via
 * useMyOrg().org?.org_id) and may be null while the org is still resolving — the
 * query is disabled until a truthy id is known, so a null org fires ZERO reads.
 */
export function ReconciliationSection({ orgId }: { orgId: string | null }) {
  // Self-contained org-scoped fetch (the parent's settlement fetch is
  // facility-scoped and carries no reconciliation data). Disabled until an org id
  // is resolved, mirroring the enabled-gating used across the app's queries.
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["analytics", "org-reconciliation", orgId],
    queryFn: () => getOrgReconciliation(orgId as string),
    enabled: !!orgId,
  });

  // A null org renders nothing (the parent also gates on orgId, but guard here so
  // the component is safe rendered in isolation): no read, no content.
  if (!orgId) return null;

  // RBAC: a 403 means the caller can see settlements but not reconciliation
  // (owner / internal-platform-admin only). Degrade to an honest owner-only note —
  // never the retryable alert, never the raw backend message verbatim.
  const isForbidden = error instanceof ApiError && error.status === 403;

  const periods = data?.periods ?? [];
  const unmatched = data?.unmatched_payout_count ?? 0;
  const failed = data?.failed_payout_count ?? 0;
  const envelopeCurrency = data?.currency ?? null;

  return (
    <Paper component="section" sx={{ overflow: "hidden" }} variant="outlined">
      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
        <Typography component="h2" variant="h6">
          Reconciliation
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2.5 }}>
        {isLoading ? (
          <Stack
            aria-label="Loading reconciliation"
            role="status"
            spacing={1}
            sx={{
              alignItems: "center",
              justifyContent: "center",
              minHeight: 128,
            }}
          >
            <CircularProgress aria-hidden size={24} />
            <Typography color="text.secondary" variant="body2">
              Loading reconciliation…
            </Typography>
          </Stack>
        ) : isForbidden ? (
          // Distinct from the retryable error state below: a 403 is not
          // retryable, so it carries no Retry button and no "couldn't load" copy.
          <Typography color="text.secondary" variant="body2">
            Reconciliation is visible to the organization owner.
          </Typography>
        ) : isError ? (
          <Alert
            action={
              <Button
                color="inherit"
                onClick={() => void refetch()}
                size="small"
              >
                Retry
              </Button>
            }
            severity="error"
          >
            We couldn&apos;t load reconciliation.
          </Alert>
        ) : periods.length === 0 ? (
          <Stack
            sx={{
              alignItems: "center",
              justifyContent: "center",
              minHeight: 128,
            }}
          >
            <Typography color="text.secondary" variant="body2">
              No reconciliation data yet.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {/* Honest, calming explanation — a small gap is EXPECTED (Stripe
                deducts its processing fees before payout), not a discrepancy to
                panic over. Worded WITHOUT the column labels (Expected/Received/
                Difference) so those labels stay unambiguous. English only
                (content-copy rule). */}
            <Typography color="text.secondary" variant="body2">
              Small gaps usually reflect Stripe&apos;s processing fees deducted
              before payout. A large gap may indicate a payout issue worth
              reviewing.
            </Typography>

            {/* unmatched / failed payout counts — surfaced ONLY when > 0. Zero is
                shown as ABSENCE, never a fabricated "0 unmatched" reassurance
                line (content-copy rule). */}
            {unmatched > 0 || failed > 0 ? (
              <Stack spacing={0.5}>
                {unmatched > 0 ? (
                  <Typography color="text.secondary" variant="body2">
                    {unmatched} {unmatched === 1 ? "payout" : "payouts"}{" "}
                    couldn&apos;t be matched to a period.
                  </Typography>
                ) : null}
                {failed > 0 ? (
                  <Typography color="text.secondary" variant="body2">
                    {failed} failed {failed === 1 ? "payout" : "payouts"} in
                    this range.
                  </Typography>
                ) : null}
              </Stack>
            ) : null}

            <ReconciliationTable
              periods={periods}
              envelopeCurrency={envelopeCurrency}
            />
          </Stack>
        )}
      </Box>
    </Paper>
  );
}

/**
 * The reconciliation rows as a Period | Expected | Received | Difference | Status
 * table — the SAME chrome as the sibling SettlementLedger / PayoutLedger (#1432).
 * Each row formats its money in its OWN `currency` (falling back to the envelope
 * currency), so a mixed-currency response (envelope currency null) still renders
 * honest per-row figures. Expected = computed_net_owed, Received = paid_out,
 * Difference = the backend `delta` VERBATIM (never a client recompute). A matched
 * period honestly shows the same figure in Expected and Received with a $0.00
 * Difference; the fixed columns compare side by side, so a non-zero delta can
 * never be mislabelled as a clean match. The Status cell stays CALM — plain
 * tone-only text (emerald for matched, muted otherwise), NEVER a destructive
 * badge: a `short` is the routine Stripe-fee case, so red would cry wolf. A
 * per-period `reconciliation_note` renders honestly in its own sub-row.
 */
function ReconciliationTable({
  periods,
  envelopeCurrency,
}: {
  periods: PeriodReconciliationResponse[];
  envelopeCurrency: string | null;
}) {
  return (
    <TableContainer>
      <Table aria-label="Reconciliation by period" size="small">
        <TableHead>
          <TableRow>
            <TableCell scope="col" sx={{ fontWeight: 700 }}>
              Period
            </TableCell>
            <TableCell align="right" scope="col" sx={{ fontWeight: 700 }}>
              Expected
            </TableCell>
            <TableCell align="right" scope="col" sx={{ fontWeight: 700 }}>
              Received
            </TableCell>
            <TableCell align="right" scope="col" sx={{ fontWeight: 700 }}>
              Difference
            </TableCell>
            <TableCell scope="col" sx={{ fontWeight: 700 }}>
              Status
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {periods.map((period) => {
            // Row currency takes precedence so a mixed-currency response (no
            // single envelope currency) still renders each row honestly.
            const currency = period.currency ?? envelopeCurrency ?? "USD";
            const note = period.reconciliation_note?.trim();
            return (
              <Fragment
                key={`${period.period_start}-${period.period_end}-${period.currency}`}
              >
                <TableRow>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {formatDate(period.period_start)} –{" "}
                    {formatDate(period.period_end)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {formatMoney(period.computed_net_owed, currency)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {formatMoney(period.paid_out, currency)}
                  </TableCell>
                  {/* The backend delta VERBATIM — signed, NEVER a recompute of
                      (received − expected); the two can legitimately diverge
                      (Stripe fees the computed net does not model). */}
                  <TableCell
                    align="right"
                    sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 700 }}
                  >
                    {formatMoney(period.delta, currency)}
                  </TableCell>
                  <TableCell>
                    <Typography
                      color={statusTone(period.reconciliation_status)}
                      component="span"
                      sx={{ fontWeight: 700 }}
                      variant="caption"
                    >
                      {humanizeStatus(period.reconciliation_status)}
                    </Typography>
                  </TableCell>
                </TableRow>
                {note ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      sx={{ color: "text.secondary", pt: 0 }}
                    >
                      {note}
                    </TableCell>
                  </TableRow>
                ) : null}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
